import React, { useState, useEffect, useMemo, useCallback } from 'react';
import WindowFrame from './components/layout/WindowFrame';
import TitleBar from './components/layout/TitleBar';
import Sidebar from './components/layout/Sidebar';
import GridView from './components/views/GridView';
import CompactGridView from './components/views/CompactGridView';
import ListView from './components/views/ListView';
import AdminModal from './components/admin/AdminModal';
import Toast from './components/common/Toast';

import { useAuth } from './hooks/useAuth';
import { useCategories } from './hooks/useCategories';
import { useProducts } from './hooks/useProducts';
import { useLinkScanner } from './hooks/useLinkScanner';
import { getCategoryInfo } from './utils/formatters';

export default function App() {
  // Navigation & View State (3 Modes of Mac Finder)
  const [activeView, setActiveView] = useState('grid'); // 'grid' | 'compact' | 'list'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all'); // 'all' | 'shopee' | 'tiktok'
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile drawer state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Admin & UI Modals
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [adminInitialTab, setAdminInitialTab] = useState('add');
  const [adminPrefillUrl, setAdminPrefillUrl] = useState('');
  const [toast, setToast] = useState(null);

  // Hooks
  const auth = useAuth();
  const { categories, reloadCategories } = useCategories();

  const productFilters = useMemo(() => ({
    category: selectedCategory,
    platform: selectedPlatform,
    searchQuery
  }), [selectedCategory, selectedPlatform, searchQuery]);

  const {
    products,
    allProducts,
    isLoading: isProductsLoading,
    reloadProducts,
    addProduct,
    removeProduct,
    purgeGreyCards,
    deleteSingleGreyCard
  } = useProducts(productFilters, auth.isAdmin);

  // Link Scanner hook
  const handleScanComplete = useCallback(async (res) => {
    await reloadProducts();
    await reloadCategories();
    setToast({
      type: 'success',
      title: 'Quét liên kết hoàn tất',
      message: `Đã kiểm tra ${res.scanned || 0} sản phẩm. Phát hiện ${res.grey_detected || 0} thẻ xám.`
    });
  }, [reloadProducts, reloadCategories]);

  const scanner = useLinkScanner(handleScanComplete);

  const handleOpenAdminTab = useCallback((tab = 'add', prefillUrl = '') => {
    setAdminInitialTab(tab);
    setAdminPrefillUrl(prefillUrl);
    setIsAdminModalOpen(true);
  }, []);

  // Keyboard shortcut: Ctrl + Shift + A to open Admin modal on Add Product
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        handleOpenAdminTab('add');
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [handleOpenAdminTab]);

  // Admin CRUD actions wrapped with feedback toast
  const handleAddProduct = useCallback(async (data) => {
    const res = await addProduct(data);
    if (res.success) {
      await reloadCategories();
      setToast({
        type: 'success',
        title: 'Thêm sản phẩm thành công',
        message: `"${data.title}" đã được thêm vào danh mục.`
      });
    }
    return res;
  }, [addProduct, reloadCategories]);

  const handleDeleteProduct = useCallback(async (id) => {
    const res = await removeProduct(id);
    if (res.success) {
      await reloadCategories();
      setToast({
        type: 'info',
        title: 'Đã xóa sản phẩm',
        message: `Sản phẩm #${id} đã được xóa khỏi hệ thống.`
      });
    }
    return res;
  }, [removeProduct, reloadCategories]);

  const handlePurgeGreyCards = useCallback(async () => {
    const res = await purgeGreyCards();
    if (res.success) {
      await reloadCategories();
      setToast({
        type: 'warning',
        title: 'Đã dọn dẹp thẻ xám',
        message: `Đã xóa sạch các sản phẩm thẻ xám khỏi kho.`
      });
    }
    return res;
  }, [purgeGreyCards, reloadCategories]);

  const handleDeleteSingleGreyCard = useCallback(async (id) => {
    const res = await deleteSingleGreyCard(id);
    if (res.success) {
      await reloadCategories();
      setToast({
        type: 'info',
        title: 'Đã xóa thẻ xám',
        message: `Sản phẩm #${id} đã được xóa.`
      });
    }
    return res;
  }, [deleteSingleGreyCard, reloadCategories]);

  return (
    <div className="w-full min-h-screen bg-[#180204] flex flex-col font-sans antialiased text-[#FFF5E6]">
      <WindowFrame>
        {/* Title Bar with Lại AI Branding & Center Search */}
        <TitleBar
          activeView={activeView}
          onViewChange={setActiveView}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isAdmin={auth.isAdmin}
          onOpenAdminModal={() => handleOpenAdminTab('add')}
          onToggleMobileSidebar={() => setIsMobileSidebarOpen(prev => !prev)}
        />

        {/* Main Split Pane: Sidebar + Viewport */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <Sidebar
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            selectedPlatform={selectedPlatform}
            onSelectPlatform={setSelectedPlatform}
            isAdmin={auth.isAdmin}
            onOpenAddCategory={() => handleOpenAdminTab('category')}
            onOpenAdminTab={handleOpenAdminTab}
            totalProducts={allProducts.length}
            greyCount={allProducts.filter(p => p.is_grey === 1).length}
            isMobileOpen={isMobileSidebarOpen}
            onCloseMobile={() => setIsMobileSidebarOpen(false)}
          />

          {/* Active View Container with Festive Crimson Theme */}
          <main className="flex-1 p-3 sm:p-5 md:p-6 overflow-y-auto bg-gradient-to-b from-[#240407] via-[#1A0205] to-[#120103] relative flex flex-col justify-between">
            <div>
              {/* Header Hero Area */}
              <div className="text-center pt-1 pb-4 max-w-4xl mx-auto select-none">
                {/* Eyebrow Pill: Mua sắm liền tay - Lộc may về nhà */}
                <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-[#3D080E] border border-[#6E141E] text-amber-200 text-xs sm:text-sm font-bold tracking-wide uppercase mb-2.5 shadow-sm">
                  <span>✦</span>
                  <span>Mua sắm liền tay - Lộc may về nhà</span>
                  <span>✦</span>
                </div>

                {/* Big Title: LÌ XÌ MAY MẮN */}
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3">
                  <span className="text-3xl sm:text-4xl">🧧</span>
                  <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight bg-gradient-to-r from-[#FFF5CE] via-[#F5A623] to-[#E58F10] bg-clip-text text-transparent drop-shadow-md">
                    LÌ XÌ MAY MẮN
                  </h1>
                </div>

                {/* Horizontal Category Quick-Pills (with Icons & Vietnamese Translations) */}
                <div className="flex items-center justify-center gap-2 flex-wrap mt-2 px-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 shadow-sm ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] shadow-amber-500/20'
                        : 'bg-[#2E060A] text-amber-200/80 hover:bg-[#480C12] hover:text-[#FFF5E6] border border-[#5A0D14]'
                    }`}
                  >
                    <span>✨</span>
                    <span>Tất cả bao lì xì</span>
                  </button>

                  {categories
                    .filter(c => c.slug !== 'all')
                    .map((cat) => {
                      const catInfo = getCategoryInfo(cat.slug || cat.name);
                      const isSelected = selectedCategory === cat.slug;
                      return (
                        <button
                          key={cat.id || cat.slug}
                          onClick={() => setSelectedCategory(cat.slug)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 shadow-sm ${
                            isSelected
                              ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] shadow-amber-500/20'
                              : 'bg-[#2E060A] text-amber-200/80 hover:bg-[#480C12] hover:text-[#FFF5E6] border border-[#5A0D14]'
                          }`}
                        >
                          <span>{catInfo.icon}</span>
                          <span>{catInfo.name}</span>
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Main Products Grid */}
              {isProductsLoading ? (
                <div className="h-64 flex items-center justify-center text-amber-200/60 text-xs">
                  <span>Đang chuẩn bị các phong bao lì xì...</span>
                </div>
              ) : (
                <>
                  {/* Mode 1: Standard Grid View */}
                  {activeView === 'grid' && (
                    <GridView products={products} />
                  )}

                  {/* Mode 2: Ultra-High Density Compact Grid View */}
                  {activeView === 'compact' && (
                    <CompactGridView products={products} />
                  )}

                  {/* Mode 3: Tabular List View */}
                  {activeView === 'list' && (
                    <ListView products={products} />
                  )}
                </>
              )}
            </div>

            {/* Clean Footer (Chúc mừng năm mới 2025 removed) */}
            <footer className="pt-8 pb-4 border-t border-[#3D080E] text-[11px] text-amber-200/60 flex flex-col sm:flex-row items-center justify-between gap-3 select-none">
              <div className="flex items-center gap-2">
                <span>🧧</span>
                <span>Lại AI • Mua sắm thông minh • An Khang Thịnh Vượng • Vạn Sự Như Ý</span>
              </div>
              <div className="flex items-center gap-4 text-amber-200/50">
                <span className="hover:text-amber-200 cursor-pointer">Chính sách lì xì</span>
                <span>•</span>
                <span className="hover:text-amber-200 cursor-pointer">Cam kết chính hãng</span>
                <span>•</span>
                <span className="hover:text-amber-200 cursor-pointer">Hỗ trợ đối tác</span>
              </div>
            </footer>
          </main>
        </div>
      </WindowFrame>

      {/* Admin Modal Dialog */}
      <AdminModal
        isOpen={isAdminModalOpen}
        onClose={() => {
          setIsAdminModalOpen(false);
          setAdminPrefillUrl('');
        }}
        auth={auth}
        products={allProducts}
        categories={categories}
        onAddProduct={handleAddProduct}
        onDeleteProduct={handleDeleteProduct}
        onPurgeGreyCards={handlePurgeGreyCards}
        onDeleteSingleGreyCard={handleDeleteSingleGreyCard}
        onReloadCategories={reloadCategories}
        scanner={scanner}
        initialTab={adminInitialTab}
        prefillUrl={adminPrefillUrl}
      />

      {/* Floating Notification Toast */}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
